import type { Sprite } from '../../gfx/atlas';
import type { Renderer } from '../../gfx/renderer';
import type { App } from '../../engine/app';
import { Screen } from '../../engine/screen';
import { button, drawLogo, type Kit, loadKit } from '../kit';
import { trackPointer, WidgetLayer } from '../widgets';
import type { MouseButton } from '../../engine/input';
import { TextColor } from '../../assets/store';
import { pendingAccountLink } from '../../net/account-link';
import { currentGateway } from '../../net/gateways';

/** A reset or confirmation link goes straight on to Battle.net, once per page. */
let followedLink = false;

/**
 * Credits, Cinematics and Exit Diablo II: the bottom of the main menu, which stays on screen
 * (and live) under the Battle.net connection and gateway dialogs, as in the original.
 */
export function addMenuFooter(ui: WidgetLayer, k: Kit, say: (s: string) => void): void {
  // Credits opens the project's About / Disclaimer / Privacy page (about.html) in a new tab.
  ui.add(button(k, 'short', 264, 505, 'Credits', () => {
    window.open('about.html', '_blank', 'noopener'); // returns null with noopener, by design
    say('Diablo II by Blizzard North. About, disclaimer and privacy: about.html');
  }));
  ui.add(button(k, 'short', 401, 505, 'Cinematics', () => say('Cinematics use a proprietary codec and are not supported.')));
  ui.add(button(k, 'wide', 264, 535, 'Exit Diablo II', () => say('Close the browser tab to exit.')));
}

/** The background, burning logo and version string every main-menu dialog sits on. */
export function drawMenuBackdrop(r: Renderer, app: App, bg: Sprite, k: Kit, t: number, version = true): void {
  r.draw(bg, 0, 0, { topLeft: true, palette: app.assets.palette.Sky });
  drawLogo(r, k, 400, 120, t);
  if (!version) return;
  // Bottom left, beside Credits, as the original prints it ("v 1.14d"); the discs' own data is 1.07.
  k.fonts.font8.draw(r, `v ${app.assets.vfs.version ?? '1.07'}`, 74, 552, { color: TextColor.White });
}

/** LoD main menu. Battle.net leads to the realm; single player runs the same server in-browser. */
export class MainMenuScreen extends Screen {
  private bg!: Sprite;
  private kit!: Kit;
  private ui = new WidgetLayer();
  private t = 0;
  toast = '';
  private toastT = 0;

  override async load(): Promise<void> {
    this.kit = await loadKit(this.app.assets, this.app.audio);
    this.bg = await this.app.assets.image('data\\global\\ui\\FrontEnd\\gameselectscreenEXP.dc6');
    const k = this.kit;
    const nav = (load: () => Promise<{ default: new () => Screen }>) => () => void load().then((m) => this.app.go(new m.default()));
    this.ui.add(button(k, 'wide', 264, 290, 'Single Player', nav(() => import('./single-player'))));
    this.ui.add(button(k, 'wide', 264, 330, 'Battle.net', nav(() => import('./bnet-connect'))));
    // The gateway bar hangs under Battle.net and opens Select Gateway.
    const gw = this.ui.add(button(k, 'narrow', 264, 365, `Gateway: ${currentGateway().name}`, async () => {
      const { showGatewayDialog } = await import('../gateway-dialog');
      const g = await showGatewayDialog(this.app);
      if (g) gw.label = `GATEWAY: ${g.name.toUpperCase()}`;
    }));
    this.ui.add(button(k, 'wide', 264, 400, 'Other Multiplayer', () => this.say('Other Multiplayer is not available — use Battle.net.')));
    addMenuFooter(this.ui, k, (s) => this.say(s));
  }
  private say(s: string): void {
    this.toast = s;
    this.toastT = 4;
  }
  override enter(): void {
    void this.app.audio.playMusic('data\\global\\music\\introedit.wav');
    if (!followedLink && pendingAccountLink()) {
      followedLink = true;
      void import('./bnet-connect').then((m) => this.app.go(new m.default()));
    }
  }
  override update(dt: number): void {
    this.t += dt;
    this.toastT = Math.max(0, this.toastT - dt);
  }
  render(r: Renderer): void {
    drawMenuBackdrop(r, this.app, this.bg, this.kit, this.t);
    // Select Gateway covers the menu (its buttons are not drawn under it).
    if (!this.app.overlays.length) this.ui.render(r, this.t);
    if (this.toastT > 0) this.kit.fonts.font16.draw(r, this.toast, 400, 452, { align: 'center', color: TextColor.Gold, alpha: Math.min(1, this.toastT) });
  }
  override onMouseDown(x: number, y: number, b: MouseButton): void {
    this.ui.onMouseDown(x, y, b);
  }
  override onMouseUp(x: number, y: number, b: MouseButton): void {
    this.ui.onMouseUp(x, y, b);
  }
  override onMouseMove(x: number, y: number): void {
    trackPointer(x, y);
    this.ui.onMouseMove(x, y);
  }
}
